import { LoadingButton } from '@mui/lab'
import { FileDownloadRounded } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'
import { useMutation } from 'react-query'
import { CustomersClient } from '../../../interactors/clients/CustomerClient'
import { useGlobalSnackbarStore } from '../../../store/GlobalSnackBarStore'
import { useDialogOpened } from '../../../utils/hooks/useDialogOpened'
import { addPrimaryCtaSx } from '../../common/addPrimaryCtaSx'

export const AssociationFullExportCard: React.FC = () => {
  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const globalSnackbarStore = useGlobalSnackbarStore()

  const exportMutation = useMutation(CustomersClient.requestFullAssociationExportJob, {
    onSuccess: async (data) => {
      closeDialog()
      if (data.alreadyRunning) {
        globalSnackbarStore.triggerInfoMessage(
          `Un export est déjà en cours pour cette association. Vérifiez votre email dans quelques minutes.`
        )
        return
      }

      globalSnackbarStore.triggerSuccessMessage(
        `Export lancé. Un lien de téléchargement vous sera envoyé par email dans quelques minutes.`
      )
    },
    onError: async (error) => {
      console.error(error)
      globalSnackbarStore.triggerErrorMessage(`Une erreur est survenue lors du lancement de l'export.`)
    },
  })

  return (
    <>
      <Card
        sx={{
          mt: 4,
          borderRadius: 4,
          boxShadow: 'none',
          border: '1px solid rgba(31, 55, 100, 0.08)',
          backgroundColor: 'rgba(255, 252, 245, 0.7)',
        }}
      >
        <CardContent sx={{ py: 2.75 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: '#203864', fontSize: { xs: 16, sm: 17 } }}>
                Exporter toutes les données
              </Typography>
              <Typography sx={{ mt: 0.75, color: '#4F5F7C', fontSize: 13.5, lineHeight: 1.5, maxWidth: 760 }}>
                Générez un export complet de votre association au format zip, avec les données métiers, les documents
                rattachés et la photo principale des animaux. Un lien de téléchargement temporaire sera envoyé par
                email.
              </Typography>
            </Box>

            <LoadingButton
              variant="contained"
              color="primary"
              onClick={openDialog}
              loading={exportMutation.isLoading}
              loadingPosition="start"
              startIcon={<FileDownloadRounded />}
              sx={{
                ...addPrimaryCtaSx,
                minHeight: 44,
                px: 3,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                alignSelf: { xs: 'flex-start', md: 'center' },
              }}
            >
              Exporter toutes les données
            </LoadingButton>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={isDialogOpen}
        onClose={exportMutation.isLoading ? undefined : closeDialog}
        PaperProps={{
          style: { borderRadius: 18, padding: 8, maxWidth: 560 },
        }}
        fullWidth
      >
        <DialogTitle sx={{ pb: 1.25 }}>Exporter toutes les données</DialogTitle>
        <DialogContent>
          <Stack spacing={1.4}>
            <Typography sx={{ color: '#42526E', lineHeight: 1.55 }}>
              Un export complet de l&apos;association va être préparé au format zip.
            </Typography>
            <Typography sx={{ color: '#42526E', lineHeight: 1.55 }}>
              Il contiendra les fiches animaux, membres et adoptants, ainsi que les documents associés et la photo
              principale de chaque animal lorsqu&apos;elle existe.
            </Typography>
            <Typography sx={{ color: '#42526E', lineHeight: 1.55 }}>
              Le fichier ne sera pas téléchargé immédiatement : un lien temporaire vous sera envoyé par email dès que
              l&apos;export sera prêt.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={closeDialog} disabled={exportMutation.isLoading} sx={{ textTransform: 'none' }}>
            Retour
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={() => exportMutation.mutate()}
            loading={exportMutation.isLoading}
            startIcon={<FileDownloadRounded />}
            sx={{
              ...addPrimaryCtaSx,
              minWidth: 120,
            }}
          >
            Exporter
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
